import React, { useEffect, useState, useRef } from "react";
import {
  Tabs,
  Input,
  Row,
  Col,
  Card,
  Tag,
  Dropdown,
  Button,
  Space,
  Divider,
  Modal,
  Slider,
  Spin,
  Tooltip,
} from "antd";
import {
  MoreOutlined,
  CheckCircleOutlined,
  CaretLeftOutlined,
  UpOutlined, // Add this import for the up arrow icon
} from "@ant-design/icons";
import axios from "axios";
import ProductListCard from "./ProductListCard.js";
import ProductDetail from "./ProductDetail"; // <-- Import ProductDetail
import "../../config";
import { useParams, useNavigate } from "react-router-dom";
import "./ProductList.css"; // Ensure you have this CSS file for styles

const { TabPane } = Tabs;
const { Search } = Input;

const ProductList = ({ refreshCartCount }) => {
  let { categoryIDQuery, categoryName } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [products, setProducts] = useState([]);
  const [showProdStatus, setShowProdStatus] = useState(false);
  const [showPrice, setShowPrice] = useState(!!localStorage.getItem("token"));

  // Modal logic for product details
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // Add this state variable after your existing state declarations
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  // Filter modal state
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [sliderRange, setSliderRange] = useState([0, 0]);
  const [pendingPriceRange, setPendingPriceRange] = useState([0, 0]); // NEW

  // Infinite scroll pagination states - ONLY LOGIC ADDITION
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Use refs to avoid stale closures in scroll handler - ONLY LOGIC ADDITION
  const pageNumberRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  // Update refs when state changes - ONLY LOGIC ADDITION
  useEffect(() => {
    pageNumberRef.current = pageNumber;
  }, [pageNumber]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    document.title = "JOJ | " + categoryName;

    // ONLY LOGIC CHANGE: Reset pagination states when category changes
    setProducts([]);
    setPageNumber(1);
    setHasMore(true);
    pageNumberRef.current = 1;
    hasMoreRef.current = true;

    // Check if this is a fresh navigation (not back navigation)
    const isFromCategoryClick = sessionStorage.getItem("isFromCategoryClick");

    if (isFromCategoryClick === "true") {
      // Fresh navigation from category - scroll to top
      window.scrollTo(0, 0);
      sessionStorage.removeItem("isFromCategoryClick");
    }
    // If not from category click (e.g., direct URL, refresh), don't force scroll to top

    // ONLY LOGIC CHANGE: Conditional API call
    const tokenString = localStorage.getItem("token");
    if (tokenString) {
      loadProductsWithPagination(1, true);
    } else {
      getListofProduct();
    }

    setShowPrice(!!tokenString);

    if (tokenString) {
      checkUserActiveStatus();
      logUserActivity();
    }

    return () => {
      sessionStorage.removeItem("productListScroll");
    };
  }, [categoryName]);

  useEffect(() => {
    const updateShowPrice = () => setShowPrice(!!localStorage.getItem("token"));

    updateShowPrice(); // run on mount/category change

    window.addEventListener("storage", updateShowPrice);
    return () => window.removeEventListener("storage", updateShowPrice);
  }, [categoryName]);

  // ONLY LOGIC ADDITION: Infinite scroll effect
  useEffect(() => {
    const tokenString = localStorage.getItem("token");

    if (!tokenString) {
      return; // No infinite scroll for public users
    }

    const handleScroll = () => {
      if (loadingRef.current || !hasMoreRef.current) {
        return;
      }

      const scrollTop = Math.max(
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );

      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight || 0,
        document.body.offsetHeight || 0,
        document.documentElement.clientHeight || 0,
        document.documentElement.scrollHeight || 0,
        document.documentElement.offsetHeight || 0
      );

      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
      const threshold = 500;

      if (distanceFromBottom <= threshold) {
        loadProductsWithPagination(pageNumberRef.current + 1, false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ONLY LOGIC ADDITION: New function for paginated API calls
  const loadProductsWithPagination = async (page, isInitial = false) => {
    if (loadingRef.current) {
      return;
    }

    if (!isInitial && !hasMoreRef.current) {
      return;
    }

    if (!isInitial && page <= pageNumberRef.current) {
      return;
    }

    setLoading(true);
    loadingRef.current = true;

    let CategoryID = sessionStorage.getItem("CategoryID");
    if (!CategoryID) {
      CategoryID = categoryIDQuery;
    }
    const tokenString = localStorage.getItem("token");

    try {
      const config = {
        method: "post",
        url: `${global.config.APIURL.BaseURL}api/Product/ListofProductByCategoryforListWithPagination?CategoryID=${CategoryID}&PageSize=${pageSize}&PageNumber=${page}`,
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      const newProducts = response.data;

      if (isInitial) {
        setProducts(newProducts);
        setPageNumber(1);
        pageNumberRef.current = 1;
      } else {
        setProducts((prevProducts) => {
          const existingIds = new Set(prevProducts.map((p) => p.ProductID));
          const uniqueNewProducts = newProducts.filter(
            (p) => !existingIds.has(p.ProductID)
          );
          return [...prevProducts, ...uniqueNewProducts];
        });

        setPageNumber(page);
        pageNumberRef.current = page;
      }

      if (newProducts.length < pageSize) {
        setHasMore(false);
        hasMoreRef.current = false;
      }

      setShowPrice(!!localStorage.getItem("token"));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
        setShowPrice(false);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          setShowProdStatus(response.data.ShowProductStatus);
          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === false
          ) {
            alert("Your session has expired. Please log in again.");
            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            window.location.reload();
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");
    let CategoryID = sessionStorage.getItem("CategoryID");
    if (!CategoryID) {
      CategoryID = categoryIDQuery;
    }
    try {
      const jsondata = JSON.stringify({
        ScreenID: "2",
        DetailID: CategoryID,
      });

      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };

      axios
        .request(config)
        .then((response) => {})
        .catch((error) => {});
    } catch (err) {}
  };

  // Helper to get the price to filter on (DealRate if valid, else SalesRate)
  const getFilterPrice = (product) => {
    if (
      product.DealRate &&
      product.DealRate > 0 &&
      product.DealRate < product.SalesRate
    ) {
      return product.DealRate;
    }
    return product.SalesRate;
  };

  // Add keyword filter states after existing filter states
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [availableKeywords, setAvailableKeywords] = useState([]);

  // Update the useEffect that sets price range to also extract keywords
  useEffect(() => {
    if (products.length > 0) {
      const prices = products.map(getFilterPrice);
      const min = Math.floor(Math.min(...prices));
      const max = Math.ceil(Math.max(...prices));
      const sliderMin = min - 10 > 0 ? min - 10 : 0;
      const sliderMax = max + 10;
      setSliderRange([sliderMin, sliderMax]);
      setPriceRange([sliderMin, sliderMax]);
      setPendingPriceRange([sliderMin, sliderMax]);

      // Extract unique keywords from all products
      const allKeywords = products.reduce((acc, product) => {
        if (product.Keywords && Array.isArray(product.Keywords)) {
          product.Keywords.forEach((keyword) => {
            if (keyword && keyword.trim() !== "") {
              acc.add(keyword.trim());
            }
          });
        }
        return acc;
      }, new Set());

      setAvailableKeywords(Array.from(allKeywords).sort());
    }
  }, [products]);

  // Update the getDisplayProducts function to include keyword filtering
  const getDisplayProducts = () => {
    let productsToFilter = isSearchMode ? searchResults : products;

    return productsToFilter.filter((product) => {
      const price = getFilterPrice(product);
      const matchesTab = activeTab === "All" || product.Status === activeTab;
      const matchesPrice = price >= priceRange[0] && price <= priceRange[1];

      // Check keyword filter
      const matchesKeywords =
        selectedKeywords.length === 0 ||
        (product.Keywords &&
          Array.isArray(product.Keywords) &&
          selectedKeywords.some((selectedKeyword) =>
            product.Keywords.some(
              (productKeyword) =>
                productKeyword &&
                productKeyword
                  .toLowerCase()
                  .includes(selectedKeyword.toLowerCase())
            )
          ));

      return matchesTab && matchesPrice && matchesKeywords;
    });
  };

  const displayProducts = getDisplayProducts();

  // Update the search input handlers
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // If search is cleared or too short, exit search mode
    if (!value || value.trim() === "" || value.trim().length <= 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  const handleSearchSubmit = (value) => {
    setSearchTerm(value);
    if (value && value.trim() !== "" && value.trim().length > 2) {
      searchProducts(value);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  // Handler for product card click
  const handleProductCardClick = (product) => {
    // Save current scroll position before opening modal
    const currentScrollY =
      window.pageYOffset || document.documentElement.scrollTop;
    setSavedScrollPosition(currentScrollY);

    // Prevent body scroll while modal is open
    document.body.style.position = "fixed";
    document.body.style.top = `-${currentScrollY}px`;
    document.body.style.width = "100%";

    setSelectedProductId(product.ProductID);
    setShowProductModal(true);
  };

  // Add this new function to handle modal close
  const handleModalClose = () => {
    setShowProductModal(false);
    setSelectedProductId(null);

    // Restore body scroll and position
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";

    // Restore scroll position
    setTimeout(() => {
      window.scrollTo(0, savedScrollPosition);
    }, 10);
  };

  const searchProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length <= 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setIsSearchMode(true);

    try {
      const tokenString = localStorage.getItem("token");
      let CategoryID = sessionStorage.getItem("CategoryID");
      if (!CategoryID) {
        CategoryID = categoryIDQuery;
      }

      const config = {
        method: "get",
        url: `${
          global.config.APIURL.BaseURL
        }api/Product/SearchProductByTextandCategory?SearchText=${encodeURIComponent(
          searchTerm
        )}&CategoryID=${CategoryID}`,
        headers: {
          "Content-Type": "application/json",
          ...(tokenString && { Authorization: "Bearer " + tokenString }),
        },
      };

      const response = await axios.request(config);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error("Error searching products:", error);
      setSearchResults([]);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
        setShowPrice(false);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  async function getListofProduct() {
    let CategoryID = sessionStorage.getItem("CategoryID");
    if (!CategoryID) {
      CategoryID = categoryIDQuery;
    }
    const tokenString = localStorage.getItem("token");

    try {
      let config;
      if (tokenString) {
        // Authenticated API
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/Product/ListofProductByCategoryforList?CategoryID=" +
            CategoryID,
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        };
      } else {
        // Public API
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/PublicData/ListofProductByCategoryforList?CategoryID=" +
            CategoryID,
        };
      }

      axios
        .request(config)
        .then((response) => {
          setProducts(response.data);
          // ONLY LOGIC CHANGE: Set hasMore to false for public users
          setHasMore(false);
          setShowPrice(!!localStorage.getItem("token")); // update after fetch
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem("token");
            setShowPrice(false); // hide price on unauthorized
          }
        });
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  }

  // Add this new state variable after your existing state declarations
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Add this useEffect to handle scroll-to-top button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300); // Show button after scrolling 300px
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Add this function to handle scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Add this CSS-in-JS style for fade animation
  const floatingButtonStyle = {
    position: "fixed",
    bottom: 30,
    right: 30,
    zIndex: 1000,
    width: 50,
    height: 50,
    borderRadius: "50%",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: "none",
    background: "#1677ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    transition: "all 0.3s ease",
    opacity: showScrollToTop ? 1 : 0,
    visibility: showScrollToTop ? "visible" : "hidden",
    transform: showScrollToTop ? "translateY(0)" : "translateY(20px)",
  };

  // Add this new state for scroll indicator
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // Add this state to detect screen size
  const [isMobile, setIsMobile] = useState(false);

  // Add this useEffect to detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768); // Consider mobile if width <= 768px
    };

    checkScreenSize(); // Check on mount
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Update the scroll indicator useEffect to only work on mobile
  useEffect(() => {
    if (showProductModal && isMobile) {
      setShowScrollIndicator(true);

      // Auto-hide after 5 seconds
      const autoHideTimer = setTimeout(() => {
        setShowScrollIndicator(false);
      }, 2800);

      const handleModalScroll = (e) => {
        const element = e.target;
        if (element.scrollTop > 50) {
          setShowScrollIndicator(false);
          clearTimeout(autoHideTimer); // Now this is properly defined
        } else {
          setShowScrollIndicator(true);
        }
      };

      const modalBody = document.querySelector(".ant-modal-body");
      if (modalBody) {
        modalBody.addEventListener("scroll", handleModalScroll);
        return () => {
          modalBody.removeEventListener("scroll", handleModalScroll);
          clearTimeout(autoHideTimer);
        };
      }

      return () => clearTimeout(autoHideTimer);
    }

    if (!showProductModal) {
      setShowScrollIndicator(true);
    }
  }, [showProductModal, isMobile]);

  return (
    <div style={{ padding: 0, overflowX: "hidden" }}>
      {" "}
      {/* Prevent horizontal scroll */}
      <Card>
        <Button
          type="primary"
          icon={<CaretLeftOutlined />}
          onClick={() => {
            sessionStorage.removeItem("productListScroll");
            navigate(-1);
            //window.location.href = "/productcategories";
          }}
        >
          Back to Category List
        </Button>
        <Divider />

        <Tabs
          defaultActiveKey="All"
          onChange={(key) => setActiveTab(key)}
          style={{ marginBottom: 16 }}
        >
          {/* <TabPane tab={`All (${products.length})`} key="All" /> */}
          <TabPane tab={`All`} key="All" />
          {showProdStatus && (
            <>
              <TabPane tab={`Available`} key="Available" />
              <TabPane tab={`Out of Stock`} key="Out of Stock" />

              {/* <TabPane
                tab={`Available (${
                  products.filter((p) => p.Status === "Available").length
                })`}
                key="Available"
              />
              <TabPane
                tab={`Out of Stock (${
                  products.filter((p) => p.Status === "Out of Stock").length
                })`}
                key="Out of Stock"
              /> */}
            </>
          )}
        </Tabs>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <Search
            placeholder="Search products... (min 3 characters)"
            onSearch={handleSearchSubmit}
            onChange={handleSearchChange}
            value={searchTerm}
            style={{ maxWidth: 300 }}
            allowClear
            loading={searchLoading}
          />
          <Button onClick={() => setFilterModalOpen(true)}>Filter</Button>
        </div>

        {/* Filter Modal */}
        <Modal
          title="Filter Products"
          open={filterModalOpen}
          onOk={() => {
            setPriceRange(pendingPriceRange);
            setFilterModalOpen(false);
          }}
          onCancel={() => setFilterModalOpen(false)}
          okText="Apply"
          cancelText="Cancel"
          footer={null}
          destroyOnClose
          width={600}
        >
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 16 }}>
              Price Range: ₹{pendingPriceRange[0]} - ₹{pendingPriceRange[1]}
            </div>
            <Slider
              range
              min={sliderRange[0]}
              max={sliderRange[1]}
              value={pendingPriceRange}
              onChange={setPendingPriceRange}
              step={1}
              tooltip={false}
            />
          </div>

          <Divider />

          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 16 }}>
              Filter by Keywords ({selectedKeywords.length} selected)
            </div>
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                padding: 12,
                backgroundColor: "#fafafa",
              }}
            >
              {availableKeywords.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {availableKeywords.map((keyword) => (
                    <Tag.CheckableTag
                      key={keyword}
                      checked={selectedKeywords.includes(keyword)}
                      onChange={(checked) => {
                        if (checked) {
                          setSelectedKeywords([...selectedKeywords, keyword]);
                        } else {
                          setSelectedKeywords(
                            selectedKeywords.filter((k) => k !== keyword)
                          );
                        }
                      }}
                      style={{
                        borderRadius: 16,
                        padding: "4px 12px",
                        fontSize: 12,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        border: "1px solid #d9d9d9",
                      }}
                    >
                      {keyword}
                    </Tag.CheckableTag>
                  ))}
                </div>
              ) : (
                <div
                  style={{ textAlign: "center", color: "#666", padding: 20 }}
                >
                  No keywords available
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginTop: 24,
            }}
          >
            <Button
              type="primary"
              onClick={() => {
                setPriceRange(pendingPriceRange);
                setFilterModalOpen(false);
              }}
              style={{ flex: 1 }}
            >
              Apply Filters
            </Button>
            <Button
              onClick={() => {
                setPendingPriceRange(sliderRange);
                setPriceRange(sliderRange);
                setSelectedKeywords([]);
                setFilterModalOpen(false);
              }}
              style={{ flex: 1 }}
            >
              Clear All Filters
            </Button>
          </div>
        </Modal>

        {/* Show applied filter info */}
        {(priceRange[0] !== sliderRange[0] ||
          priceRange[1] !== sliderRange[1] ||
          selectedKeywords.length > 0) && (
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            {/* Price filter info */}
            {(priceRange[0] !== sliderRange[0] ||
              priceRange[1] !== sliderRange[1]) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#e0fffe",
                  border: "1px solid #0d9e99",
                  borderRadius: 6,
                  padding: "6px 12px",
                  color: "#0d9e99",
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                <span>
                  Price: ₹{priceRange[0]} - ₹{priceRange[1]}
                </span>
                <Button
                  size="small"
                  type="link"
                  style={{ color: "red", padding: 0, minWidth: "auto" }}
                  onClick={() => {
                    setPriceRange(sliderRange);
                    setPendingPriceRange(sliderRange);
                  }}
                >
                  ✕
                </Button>
              </div>
            )}

            {/* Keywords filter info */}
            {selectedKeywords.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#f0f9ff",
                  border: "1px solid #0ea5e9",
                  borderRadius: 6,
                  padding: "6px 12px",
                  color: "#0ea5e9",
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                <span>Keywords: {selectedKeywords.join(", ")}</span>
                <Button
                  size="small"
                  type="link"
                  style={{ color: "red", padding: 0, minWidth: "auto" }}
                  onClick={() => setSelectedKeywords([])}
                >
                  ✕
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Add search mode indicator */}
        {isSearchMode && (
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#f0f9ff",
              border: "1px solid #0ea5e9",
              borderRadius: 6,
              padding: "8px 16px",
              color: "#0ea5e9",
              fontWeight: 500,
              fontSize: 15,
              width: "fit-content",
            }}
          >
            <span>
              🔍 Search Results for "{searchTerm}" ({searchResults.length}{" "}
              found)
            </span>
            <Button
              size="small"
              type="link"
              style={{ color: "#ef4444", padding: 0 }}
              onClick={() => {
                setSearchTerm("");
                setIsSearchMode(false);
                setSearchResults([]);
              }}
            >
              Clear Search
            </Button>
          </div>
        )}

        {/* Add indicator when user is typing but hasn't reached minimum length */}
        {searchTerm &&
          searchTerm.trim().length > 0 &&
          searchTerm.trim().length <= 2 &&
          !isSearchMode && (
            <div
              style={{
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: 6,
                padding: "8px 16px",
                color: "#f59e0b",
                fontWeight: 500,
                fontSize: 15,
                width: "fit-content",
              }}
            >
              <span>
                ⌨️ Type at least 3 characters to search (
                {searchTerm.trim().length}/3)
              </span>
            </div>
          )}

        <Row
          gutter={[16, 16]}
          justify="center"
          style={{
            margin: 0,
            overflowX: "hidden", // Prevent horizontal scroll on Row
          }}
        >
          {displayProducts.map((product) => (
            <Col key={product.ProductID} xs={12} sm={12} md={8} lg={6}>
              <ProductListCard
                product={product}
                key={product.ProductID}
                showProdStatus={showProdStatus}
                showPrice={showPrice}
                onClick={handleProductCardClick}
              />
            </Col>
          ))}
        </Row>

        {/* ONLY VIEW ADDITION: Loading indicator for infinite scroll */}
        {searchLoading && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 8, color: "#666" }}>
              Searching products...
            </div>
          </div>
        )}

        {/* Show infinite scroll loading only when NOT in search mode */}
        {loading && !isSearchMode && localStorage.getItem("token") && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 8, color: "#666" }}>
              Loading more products...
            </div>
          </div>
        )}

        {
          /* Show no results message for search */
          isSearchMode &&
            !searchLoading &&
            searchResults.length === 0 &&
            searchTerm && (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                <div style={{ fontSize: 18, marginBottom: 8 }}>
                  No products found
                </div>
                <div>Try different keywords or check spelling</div>
              </div>
            )
        }

        {/* ONLY VIEW ADDITION: End of data indicator */}
        {!hasMore &&
          products.length > 0 &&
          !isSearchMode &&
          localStorage.getItem("token") && (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#666" }}
            >
              You've reached the end of all products
            </div>
          )}

        {/* Product Detail Modal */}
        <Modal
          open={showProductModal}
          onCancel={handleModalClose} // Use the new handler
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "16px 0 0 0",
              }}
            >
              <Button
                type="primary"
                style={{
                  minWidth: 120,
                  borderRadius: 6,
                  background: "#ff4d4f", // Ant Design red
                  borderColor: "#ff4d4f",
                }}
                onClick={handleModalClose} // Use the new handler
              >
                Close
              </Button>
            </div>
          }
          width="95%"
          style={{ maxWidth: 900 }}
          destroyOnClose
          styles={{
            body: {
              padding: 0,
              position: "relative",
              maxHeight: "90%",
              overflowY: "auto",
              // Hide scrollbar on desktop, show on mobile
              scrollbarWidth: isMobile ? "auto" : "none", // Firefox
              msOverflowStyle: isMobile ? "auto" : "none", // IE/Edge
              // WebKit browsers (Chrome, Safari)
              ...(isMobile
                ? {}
                : {
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }),
            },
            mask: { position: "fixed" },
          }}
          centered={false}
          maskClosable={true}
          keyboard={true}
          getContainer={() => document.body}
          className={!isMobile ? "hide-scrollbar" : ""} // Add conditional class
        >
          {showProductModal && selectedProductId && (
            <>
              <ProductDetail
                refreshCartCount={refreshCartCount}
                productIdQuery={selectedProductId}
                isModal
                setDocumentTitle={() => {}}
              />

              {/* Only show scroll indicator on mobile */}
              {showScrollIndicator && isMobile && (
                <div
                  style={{
                    position: "absolute",
                    top: 550,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 1001,
                    width: 200,
                    background: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: 50,
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    animation: "bounce 2s infinite",
                    pointerEvents: "none",
                  }}
                >
                  <center>
                    <span>Scroll down for more details</span>
                    <span style={{ fontSize: 16 }}>⬇️</span>
                  </center>
                </div>
              )}
            </>
          )}
        </Modal>

        {/* Show login prompt if not logged in */}
        {!localStorage.getItem("token") && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              margin: "32px 0 150px 0",
            }}
          >
            <Card
              style={{
                width: "100%",
                maxWidth: 400,
                borderRadius: 10,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                textAlign: "center",
                padding: 0,
              }}
              styles={{ padding: "24px 16px" }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#1677ff",
                }}
              >
                Want to see all products?
              </div>
              <div
                style={{
                  fontSize: 16,
                  color: "#444",
                  marginBottom: 20,
                }}
              >
                Please log in to view the complete catalogue of products.
              </div>
              <Button
                type="primary"
                size="large"
                style={{
                  width: "100%",
                  fontWeight: 600,
                  fontSize: 18,
                  borderRadius: 6,
                }}
                onClick={() => (window.location.href = "/login")}
              >
                Login to View All Products
              </Button>
            </Card>
          </div>
        )}

        {/* Floating Scroll to Top Button */}
        {showScrollToTop && (
          <Tooltip title="Scroll to top" placement="left">
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<UpOutlined />}
              onClick={scrollToTop}
              style={{
                position: "fixed",
                bottom: 30,
                right: 30,
                zIndex: 1000,
                width: 50,
                height: 50,
                borderRadius: "50%",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                border: "none",
                background: "#1677ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.1)";
                e.target.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
              }}
            />
          </Tooltip>
        )}
      </Card>
    </div>
  );
};

export default ProductList;

/* Add CSS for bounce animation */
